import * as React from 'react';

import { useQuery } from '@apollo/client';
import PropTypes from 'prop-types';
import type { LegacyContextType } from 'types/legacy-context-types';

import { Button, TextField, Typography2 } from '@coursera/cds-core';

import { Spacer } from 'bundles/enterprise-ui/components';
import PrivateAuthoringPartner from 'bundles/internal-enterprise/components/InternalThirdPartyOrganizationPage/PrivateAuthoringPartner';
import GetPartnerBySlugForPrivateAuthoringExistingPartner from 'bundles/internal-enterprise/components/InternalThirdPartyOrganizationPage/queries/GetPartnerBySlugForPrivateAuthoringExistingPartner.graphql';

import _t from 'i18n!nls/internal-enterprise';

import type {
  GetPartnerBySlugForPrivateAuthoringExistingPartnerQuery,
  GetPartnerBySlugForPrivateAuthoringExistingPartnerQueryVariables,
} from './queries/__generated__/GetPartnerBySlugForPrivateAuthoringExistingPartner';

const linkExistingPartner = ({
  thirdPartyOrganizationId,
  partnerId,
}: {
  thirdPartyOrganizationId: string;
  partnerId: number;
}) =>
  fetch(`/api/enterpriseAuthoringMappings.v1?action=linkExistingPartner`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      organizationId: thirdPartyOrganizationId,
      partnerId,
    }),
  });

type PropsFromCaller = {
  thirdPartyOrganizationId: string;
  onPartnerAdded: () => void;
};

type PropsFromContainer = {
  onSlugChange: (slug: string) => void;
  loading: boolean;
  partner?: {
    id: string;
    name: string;
    shortName: string;
    squareLogo: string;
    partnerType: number;
    display: boolean;
    __typename: 'PartnersV1';
  };
};

type Props = PropsFromCaller & PropsFromContainer;

type State = {
  shortName: string;
  hasSearched: boolean;
};

const INITIAL_STATE = {
  shortName: '',
  hasSearched: false,
};

class PrivateAuthoringExistingPartner extends React.Component<Props, State> {
  static contextTypes = {
    addToast: PropTypes.func.isRequired,
  };

  declare context: LegacyContextType<typeof PrivateAuthoringExistingPartner.contextTypes>;

  state: State = INITIAL_STATE;

  handleSearchQueryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      shortName: event.target.value,
    });
  };

  handleSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const { addToast } = this.context;
    const { shortName } = this.state;
    if (!shortName) {
      addToast(_t('Enter a query'));
    } else {
      this.props.onSlugChange(shortName);
      this.setState({ hasSearched: true });
    }
  };

  handleAddPartner = (partnerId: number) => {
    const { addToast } = this.context;
    const { thirdPartyOrganizationId, onPartnerAdded } = this.props;

    if (
      // eslint-disable-next-line no-alert
      !window.confirm(
        _t(
          `Do you want to create a link between the selected Enterprise and Educator organizations?  Once the link is established, this private authoring configuration can only be removed or modified by Engineering.`
        )
      )
    ) {
      return;
    }
    linkExistingPartner({
      thirdPartyOrganizationId,
      partnerId,
    })
      .then((_) => _.json())
      .then((response) => {
        if ('errorCode' in response) {
          return Promise.reject(response);
        }
        addToast(_t('Added!'));
        onPartnerAdded();
        this.setState(INITIAL_STATE);
        return Promise.resolve();
      })
      .catch(() => {
        addToast(_t('Failed!'));
      });
  };

  render() {
    const { loading, partner } = this.props;
    const { hasSearched, shortName } = this.state;

    return (
      <div>
        <form onSubmit={this.handleSearch}>
          <TextField
            fullWidth
            label={_t('Search by slug (exact match)')}
            value={shortName}
            onChange={this.handleSearchQueryChange}
          />
          <Spacer size={24} />
          <Button type="submit" variant={shortName && partner ? 'secondary' : 'primary'}>
            {_t('Search')}
          </Button>
        </form>
        <Spacer size={24} />
        {shortName && loading && <Typography2 component="p">{_t('Searching...')}</Typography2>}
        {hasSearched && !loading && !partner && (
          <Typography2 component="p">{_t('Not found (exact match)')} </Typography2>
        )}
        {partner && (
          <PrivateAuthoringPartner key={partner.id} partner={partner}>
            <Spacer size={24} />
            <Button
              onClick={() => {
                this.handleAddPartner(Number(partner.id));
              }}
              variant="primary"
            >
              {_t('Add')}
            </Button>
          </PrivateAuthoringPartner>
        )}
        <Spacer size={24} />
        <small>
          {_t(
            'Linking an existing partner will not change it to a private authoring partner if it was not one originally.'
          )}
        </small>
      </div>
    );
  }
}

const PrivateAuthoringExistingPartnerContainer = (props: PropsFromCaller) => {
  const [slug, setSlug] = React.useState('');
  const { data, loading } = useQuery<
    GetPartnerBySlugForPrivateAuthoringExistingPartnerQuery,
    GetPartnerBySlugForPrivateAuthoringExistingPartnerQueryVariables
  >(GetPartnerBySlugForPrivateAuthoringExistingPartner, {
    skip: !slug,
    variables: { slug },
    context: { clientName: 'gatewayGql' },
  });
  const partner = data?.Partner?.queryBySlug;
  return (
    <PrivateAuthoringExistingPartner
      onSlugChange={setSlug}
      loading={loading}
      // @ts-expect-error URL: string
      partner={
        partner
          ? {
              id: partner.id,
              name: partner.name,
              shortName: partner.slug,
              squareLogo: partner.squareLogo,
              partnerType: ['UNIVERSITY', 'INDUSTRY', 'PRIVATE_AUTHORING'].indexOf(partner.partnerType) + 1,
              display: partner.display,
              __typename: 'PartnersV1',
            }
          : undefined
      }
      {...props}
    />
  );
};

export default PrivateAuthoringExistingPartnerContainer;
